import type { PlaybackStream, PlaybackStreamQuality } from '@/features/live/types';

export type ConnectionTier = 'high' | 'medium' | 'low' | 'very-low';

export const QUALITY_ORDER: PlaybackStreamQuality[] = ['P360', 'P480', 'P720', 'P1080'];

// Échelle continue : chaque cran descend d'une rendition, pas de falaise (720 → 480 → 360).
const CEILING_BY_CONNECTION: Record<ConnectionTier, PlaybackStreamQuality> = {
  'very-low': 'P360',
  low: 'P480',
  medium: 'P720',
  high: 'P1080',
};

const rank = (quality: PlaybackStreamQuality): number => QUALITY_ORDER.indexOf(quality);

/** Qualité la plus haute qu'un palier de connexion soutient. */
export const qualityForConnection = (connection: ConnectionTier): PlaybackStreamQuality =>
  CEILING_BY_CONNECTION[connection];

const lowerCeiling = (a: PlaybackStreamQuality, b: PlaybackStreamQuality): PlaybackStreamQuality =>
  rank(a) <= rank(b) ? a : b;

/**
 * Tant que le débit n'a pas été mesuré sur les segments, le palier vient d'un ping qui ne dit rien
 * de la capacité : on garde le seuil prudent, sous la largeur de la source 720p (1280).
 */
const P1080_MIN_FRAME_PIXELS = 1000;

/**
 * Une fois le débit réellement mesuré, un téléphone en portrait (390 px à densité plafonnée 2, soit
 * 780) devient éligible : le 1080p y est redimensionné, mais répartir les spectateurs sur deux flux
 * vaut mieux que de concentrer tous les mobiles sur le seul 720p.
 */
const P1080_MIN_FRAME_PIXELS_MEASURED = 700;

/** Plafond utile pour la surface réellement affichée. Ne descend jamais sous le 720p. */
function ceilingForFrame(framePixels?: number, connectionMeasured = false): PlaybackStreamQuality {
  if (framePixels == null) return 'P1080';
  const min = connectionMeasured ? P1080_MIN_FRAME_PIXELS_MEASURED : P1080_MIN_FRAME_PIXELS;
  return framePixels >= min ? 'P1080' : 'P720';
}

export type StreamSelectionOptions = {
  /**
   * Pixels physiques occupés par le cadre vidéo, soit sa largeur multipliée par la densité d'écran.
   * On ne teste jamais « est-ce un mobile » : un pliable déplié a un grand cadre et reçoit le flux
   * haut, un flip fermé a un petit cadre et reçoit le 720p, sans liste d'appareils à maintenir.
   */
  framePixels?: number;
  /** Qualités constatées inexploitables, à ne plus proposer tant qu'elles n'ont pas été retentées. */
  avoidedQualities?: PlaybackStreamQuality[];
  /** Vrai dès que le palier repose sur le débit observé et non plus sur un ping. */
  connectionMeasured?: boolean;
  /** Plafond posé par le repli après un gel dû au débit du spectateur. */
  bandwidthCeiling?: PlaybackStreamQuality | null;
};

/** Parmi des flux tous écartés, celui qui l'a été le plus tôt : le plus proche d'être retenté. */
function leastRecentlyAvoided(
  pool: PlaybackStream[],
  avoided: PlaybackStreamQuality[],
): PlaybackStream {
  const oldest = avoided.find((quality) => pool.some((stream) => stream.quality === quality));
  return pool.find((stream) => stream.quality === oldest) ?? pool[0];
}

export function selectStream(
  streams: PlaybackStream[] | undefined,
  connection: ConnectionTier,
  options?: StreamSelectionOptions,
): PlaybackStream | null {
  const ready = (streams ?? []).filter((stream) => stream.ready);
  if (ready.length === 0) return null;

  const sorted = [...ready].sort((a, b) => rank(a.quality) - rank(b.quality));
  const bandwidthCeiling = options?.bandwidthCeiling ?? null;
  const connectionCeiling =
    bandwidthCeiling === null
      ? CEILING_BY_CONNECTION[connection]
      : lowerCeiling(CEILING_BY_CONNECTION[connection], bandwidthCeiling);

  // Un débit mesuré rend le plafond de connexion infranchissable : au-dessus, le flux gèlerait en
  // continu. Un palier tiré d'un ping ne dit rien de la capacité, il reste franchissable.
  const affordable = sorted.filter((stream) => rank(stream.quality) <= rank(connectionCeiling));
  const hardCeiling = options?.connectionMeasured === true || bandwidthCeiling !== null;
  const pool = hardCeiling && affordable.length > 0 ? affordable : sorted;

  const avoided = options?.avoidedQualities ?? [];
  const usable = pool.filter((stream) => !avoided.includes(stream.quality));
  // Tout écarter reviendrait à ne rien jouer : un flux imparfait vaut mieux qu'un écran figé.
  if (usable.length === 0) return leastRecentlyAvoided(pool, avoided);

  const ceiling = lowerCeiling(
    connectionCeiling,
    ceilingForFrame(options?.framePixels, options?.connectionMeasured),
  );

  const underCeiling = usable.filter((stream) => rank(stream.quality) <= rank(ceiling));
  // Rien sous le plafond (ex. connexion basse mais seul le 1080 reste) : lire reste prioritaire.
  return underCeiling.length > 0 ? underCeiling[underCeiling.length - 1] : usable[0];
}

/**
 * URL à jouer une fois la sélection faite. Les champs legacy `hlsLiveUrl` sont le repli des
 * anciennes sessions sans `streams[]` ; ils ne portent aucune vitalité, donc une session qui
 * publie des flux mais n'en a plus aucun de prêt doit rendre la main (coupure) plutôt que d'y
 * retomber et de coller le spectateur à une URL morte.
 */
export function resolveLivePlaybackUrl(
  streams: PlaybackStream[] | undefined,
  selected: PlaybackStream | null,
  legacyUrl: string | undefined,
): string {
  if (selected?.hlsLiveUrl) return selected.hlsLiveUrl;
  if ((streams?.length ?? 0) > 0) return '';
  return legacyUrl?.trim() ?? '';
}

export const selectStreamUrl = (
  streams: PlaybackStream[] | undefined,
  connection: ConnectionTier,
  options?: StreamSelectionOptions,
): string | null => selectStream(streams, connection, options)?.hlsLiveUrl ?? null;
